import React, { FunctionComponent, useState } from 'react';
import Logo from '../logo';
import Container from '../container';
import { ROUTES } from '../../constants/routes';
import { useId } from 'react-id-generator';
import Button from '../button';
import { ReactComponent as MenuIcon } from 'assets/icons/Menu.svg';
import Menu from './menu';
import classNames from 'classnames';
import { useAuth } from 'providers/auth';
import { useNavigate } from 'hooks/useNavigate';
import CititiesSelect from './cities-select';
import UserButton from './user-button';
import { useProtect } from 'hooks/useProtect';
import { generatePath } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../language-switcher';
import { useCities } from '../../providers/cities';
import { Box, Tooltip } from '@mui/material';

interface HeaderProps {}

const Header: FunctionComponent<HeaderProps> = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const { user, openLoginPage } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { selectedCity } = useCities();
  const links = [
    {
      handle: () => navigate(ROUTES.messages),
      label: t('header.reports'),
      isCurrent: ROUTES.messages === window.location.pathname,
    },
    {
      handle: () => navigate(ROUTES.stats),
      label: t('header.statistics'),
      isCurrent: ROUTES.stats === window.location.pathname,
    },
    { handle: openLoginPage, label: t('header.login'), hide: !!user },
  ];
  const protect = useProtect();

  const ids = useId(links.length);

  const newReportButton = () => {
    if (selectedCity?.problem_submission_message) {
      return (
        <Tooltip title={selectedCity?.problem_submission_message}>
          <Box>
            <Button
              className='hidden @[1000px]:block pointer-events-none disabled:opacity-50'
              disabled={true}
            >
              {t('header.newReport')}
            </Button>
          </Box>
        </Tooltip>
      );
    }

    return (
      <Button
        className='hidden @[1000px]:block'
        onClick={() =>
          protect(() => navigate(generatePath(ROUTES.newMessage, { '*': '0' })))
        }
      >
        {t('header.newReport')}
      </Button>
    );
  };

  return (
    <>
      <Menu open={menuOpen} onClose={() => setMenuOpen(false)} links={links} />
      <Container className='bg-theme-white border-b border-theme-border sticky top-0 z-50'>
        <div className='flex items-center h-20 @container'>
          <div className='flex items-center'>
            <div className='@[1000px]:pr-7 @[1000px]:mr-3 @[1000px]:border-r'>
              <Logo className='cursor-pointer' onClick={() => navigate('/')} />
            </div>
            <CititiesSelect className='hidden @[1000px]:block' />
          </div>
          <div className='flex-grow justify-end flex items-center '>
            <div className='flex mr-4 hidden @[1000px]:flex'>
              <LanguageSwitcher />
            </div>
            <div className='flex mr-4'>
              {links.map((link, i) =>
                link.hide ? null : (
                  <div
                    onClick={link.handle}
                    className={classNames(
                      'px-4 cursor-pointer hidden @[1000px]:block text-[15px] hover:text-theme-main',
                      {
                        'font-semibold': link.isCurrent,
                      },
                    )}
                    key={ids[i]}
                  >
                    {link.label}
                  </div>
                ),
              )}
            </div>
            {newReportButton()}
            {!user ? null : (
              <div
                onClick={() => navigate(ROUTES.profile)}
                className='hidden @[1000px]:flex items-center h-[52px] rounded-full border border-transparent hover:border-theme-border pl-2 pr-4 ml-3 cursor-pointer select-none'
              >
                <UserButton />
              </div>
            )}
            <MenuIcon
              onClick={() => setMenuOpen(true)}
              className='block @[1000px]:hidden cursor-pointer select-none'
            />
          </div>
        </div>
      </Container>
    </>
  );
};

export default Header;
